import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  domError,
  errorText,
  formatHash,
  formatError,
  result,
  serializeNumber,
  validation,
  validationError,
} from './shared.ts';

function normalizeError(error: unknown): RawData {
  const detail = domError(error);
  return { name: detail.name, message: detail.message };
}

async function collect(): Promise<RawData> {
  const math = Object.fromEntries(
    Object.entries({
      acos: Math.acos(0.123),
      asinh: Math.asinh(1),
      atan2: Math.atan2(1e-310, -1e-310),
      expm1: Math.expm1(1),
      hypot: Math.hypot(1e-300, 1e300),
      log1p: Math.log1p(1e-15),
      negativeZero: -0,
      nan: Number.NaN,
    }).map(([key, value]) => [key, serializeNumber(value)]),
  );
  const typedArrays = {
    float64: [...new Uint8Array(new Float64Array([Math.PI]).buffer)]
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join(''),
    uint32: [...new Uint32Array([0, 1, 0xffffffff])].join(','),
  };
  const wasmSupported = typeof WebAssembly == 'object';
  const wasm = {
    supported: wasmSupported,
    pageSize: wasmSupported
      ? new WebAssembly.Memory({ initial: 1 }).buffer.byteLength
      : null,
  };
  const errors: Record<string, RawData> = {};
  try {
    JSON.parse('{');
  } catch (error) {
    errors.json = normalizeError(error);
  }
  return { math, typedArrays, wasm, errors };
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return formatHash(rawData);
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validation([]);
}

export const javascriptRuntimeTest: TestDefinition = {
  id: 'javascriptRuntime',
  title: 'JavaScript runtime',
  category: 'shared',
  format,
  run,
  validate,
};
